import { useState, useEffect, useMemo, useCallback } from "react";

import { usePost } from "../hooks/usePost";
import { useToast } from "../store/ToastProvider";
import { formatReadable } from "../utils/formattingUtils";

import { MESSAGES } from "../messages";
import { STEWARDSHIP_SCREENER_PAGE_SIZE } from "../pages/StewardshipHomepage/constants";

import { Filter as Option } from "../types/PageFilterTypes";
import {
  CommonDataItem,
  CommonDataRequestType,
  DistinctFundsRequest,
  FundsWManagerItem,
  ManagerItem,
} from "../types/CommonDataRequestTypes";
import { PaginationParams } from "../types/ScreenerTypes";
import { StewardshipFilterParam } from "../models/stewardshipModels";

export const DEFAULT_PAGINATION: PaginationParams = {
  page: 1,
  page_size: STEWARDSHIP_SCREENER_PAGE_SIZE,
};

/**
 * Custom hook for the Stewardship Screener component.
 * Manages state and data fetching for funds, managers, and engaged companies options,
 * and handles filter parameters and pagination for the stewardship screener.
 */
export const useStewardshipScreener = (): UseStewardshipScreenerReturn => {
  const { addToast } = useToast();

  // ============ State ============
  // Options (fetched from API)
  const [fundOptions, setFundOptions] = useState<Option[]>([]);
  const [managerOptions, setManagerOptions] = useState<Option[]>([]);
  const [engagedCompaniesOptions, setEngagedCompaniesOptions] = useState<
    Option[]
  >([]);

  const [selectedManagers, setSelectedManagers] = useState<Option[]>([]);
  const [selectedFunds, setSelectedFunds] = useState<Option[]>([]);
  const [selectedCompanies, setSelectedCompanies] = useState<Option[]>([]);
  const [pagination, setPagination] =
    useState<PaginationParams>(DEFAULT_PAGINATION);

  const [error, setError] = useState<boolean>(false);

  // ============ API Hooks ============
  const {
    error: distinctFundsError,
    sendData: fetchDistinctFunds,
    isLoading: isDistinctFundsLoading,
  } = usePost<FundsWManagerItem[], CommonDataItem<DistinctFundsRequest>>();

  const {
    error: managerError,
    sendData: fetchManagers,
    isLoading: isManagerLoading,
  } = usePost<ManagerItem[], null>();

  const {
    error: engagedCompaniesError,
    sendData: fetchEngagedCompanies,
    isLoading: isEngagedCompaniesLoading,
  } = usePost<{ company: string; name: string }[], StewardshipFilterParam>();

  // ============ Derived Values (not state!) ============
  const filteredFundOptions = useMemo(() => {
    if (selectedManagers.length === 0) return fundOptions;
    return fundOptions.filter((fund) =>
      selectedManagers.some((manager) => fund.selectorName === manager.key),
    );
  }, [fundOptions, selectedManagers]);

  const filterParams = useMemo<StewardshipFilterParam>(
    () => ({
      manager_id: selectedManagers.map((manager) => String(manager.key)),
      fund_id: selectedFunds.map((fund) => String(fund.key)),
      compass_cid: selectedCompanies.map((company) => String(company.key)),
      pagination,
    }),
    [selectedManagers, selectedFunds, selectedCompanies, pagination],
  );

  const isLoading =
    isDistinctFundsLoading || isManagerLoading || isEngagedCompaniesLoading;

  // ============ Handlers (with inline resets) ============
  const handleSelectManagers = useCallback((managers: Option[]) => {
    setSelectedManagers(managers);
    setSelectedFunds((funds) =>
      funds.filter((fund) =>
        managers.some((manager) => fund.selectorName === manager.key),
      ),
    );
    setSelectedCompanies([]);
    setPagination(DEFAULT_PAGINATION);
  }, []);

  const handleSelectFunds = useCallback((funds: Option[]) => {
    setSelectedFunds(funds);
    setSelectedCompanies([]);
    setPagination(DEFAULT_PAGINATION);
  }, []);

  const handleSelectCompanies = useCallback((companies: Option[]) => {
    setSelectedCompanies(companies);
    setPagination(DEFAULT_PAGINATION);
  }, []);

  const handlePagination = useCallback((newPagination: PaginationParams) => {
    setPagination(newPagination);
  }, []);

  // ============ Data Fetching ============
  const buildFundsOptions = async (): Promise<void> => {
    const data = await fetchDistinctFunds("GET_COMMON_DATA", {
      name: CommonDataRequestType.DISTINCT_FUNDS_W_MANAGER,
      item: { distinct_funds: null },
    });
    if (!data) return;
    const options: Option[] = data
      .filter((fund) => !fund.is_index)
      .map((fund) => ({
        key: fund.fund_id,
        displayValue: fund.fund_name,
        selectorName: fund.manager_id,
      }));
    setFundOptions(options);
  };

  const buildManagerOptions = async (): Promise<void> => {
    const data = await fetchManagers("GET_ALL_MANAGERS", null);
    if (!data) return;
    const options: Option[] = data.map((manager) => ({
      key: manager.manager_id,
      displayValue: manager.manager_name,
      selectorName: manager.image,
    }));
    setManagerOptions(options);
  };

  const fetchCompanyOptions = useCallback(
    async (managerIds: string[], fundIds: string[]): Promise<void> => {
      const data = await fetchEngagedCompanies(
        "GET_STEWARDSHIP_SCREENER_CONFIG",
        {
          manager_id: managerIds,
          fund_id: fundIds,
        },
      );
      if (!data) return;
      const options: Option[] = data.map((company) => ({
        key: company.company,
        displayValue: formatReadable(company.name),
      }));
      setEngagedCompaniesOptions(options);
    },
    [fetchEngagedCompanies],
  );

  // ============ Effects (minimal!) ============
  // 1. Fetch static options on mount
  useEffect(() => {
    buildFundsOptions();
    buildManagerOptions();
    fetchCompanyOptions([], []);
  }, []);

  // 2. Fetch company options when manager/fund filters change
  useEffect(() => {
    fetchCompanyOptions(
      selectedManagers.map((manager) => String(manager.key)),
      selectedFunds.map((fund) => String(fund.key)),
    );
  }, [selectedManagers, selectedFunds, fetchCompanyOptions]);

  // 3. Handle errors
  useEffect(() => {
    if (distinctFundsError || managerError || engagedCompaniesError) {
      setError(true);
      addToast(
        MESSAGES.STEWARDSHIP_HOMEPAGE.FETCH_OPTIONS_ERROR(
          distinctFundsError
            ? "funds"
            : managerError
              ? "managers"
              : "engaged companies",
        ),
        "error",
      );
    }
  }, [distinctFundsError, managerError, engagedCompaniesError, addToast]);

  return {
    // Options
    fundOptions,
    filteredFundOptions,
    managerOptions,
    engagedCompaniesOptions,
    // Selected values
    selectedManagers,
    selectedFunds,
    selectedCompanies,
    // Derived
    filterParams,
    // Handlers
    setSelectedManagers: handleSelectManagers,
    setSelectedFunds: handleSelectFunds,
    setSelectedCompanies: handleSelectCompanies,
    setScreenerPagination: handlePagination,
    // Status
    isLoading,
    error,
  };
};

type UseStewardshipScreenerReturn = {
  fundOptions: Option[];
  filteredFundOptions: Option[];
  managerOptions: Option[];
  engagedCompaniesOptions: Option[];
  filterParams: StewardshipFilterParam;
  selectedManagers: Option[];
  selectedFunds: Option[];
  selectedCompanies: Option[];
  setSelectedManagers: (options: Option[]) => void;
  setSelectedFunds: (options: Option[]) => void;
  setSelectedCompanies: (options: Option[]) => void;
  setScreenerPagination: (pagination: PaginationParams) => void;
  isLoading: boolean;
  error: boolean;
};
